import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { LogOut, User, Mail, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/auth/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Account deleted successfully');
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto p-6 md:p-12 space-y-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold mb-2">My Profile</h1>
          <p className="text-lg text-muted-foreground">Manage your account settings</p>
        </div>

        <Card className="shadow-sm border-border/50" data-testid="profile-card">
          <CardHeader className="text-center pb-6">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-primary to-primary/60 rounded-full flex items-center justify-center mb-4">
              <User className="w-12 h-12 text-white" />
            </div>
            <CardTitle className="text-3xl">{user?.name}</CardTitle>
            <p className="text-muted-foreground">Student</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="bg-secondary/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <User className="w-5 h-5 text-primary" />
                  <p className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Name</p>
                </div>
                <p className="text-lg font-medium ml-8">{user?.name}</p>
              </div>

              <div className="bg-secondary/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="w-5 h-5 text-primary" />
                  <p className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Email</p>
                </div>
                <p className="text-lg font-medium ml-8">{user?.email}</p>
              </div>
            </div>

            <div className="border-t pt-6 space-y-3">
              <Button
                onClick={handleLogout}
                variant="destructive"
                className="w-full rounded-full h-12"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={() => setShowDeleteDialog(true)}
                  variant="outline"
                  className="text-destructive hover:bg-destructive/10 border-destructive/30 rounded-full px-6"
                  data-testid="delete-account-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Account
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delete Account Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-destructive">Delete Account</DialogTitle>
              <DialogDescription className="pt-4 space-y-3">
                <p className="font-semibold">Are you absolutely sure?</p>
                <p>This action cannot be undone. This will permanently delete your account and remove all your data from our servers, including:</p>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li>Your timetable and class schedule</li>
                  <li>All assignments and tasks</li>
                  <li>Personal notes and study progress</li>
                  <li>Account information</li>
                </ul>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="rounded-full"
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete My Account'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Profile;
